// Relationship categories — used for form field visibility and validation.
export const RELATIONSHIPS_WITH_LOCATION = [
  'personOrOrganization',
  'mortgageeCoOwner',
  'managerOrLessorOfPremises',
  'lossPayee',
  'landlord',
  'occupant'
];
export const RELATIONSHIPS_WITH_EQUIPMENT = ['lessorOfLeasedEquipment'];
export const RELATIONSHIPS_WITH_LOCATION_AND_RISKS = ['trustCorpsEtc'];
export const RELATIONSHIPS_WITH_LOCATION_RISKS_AND_EQUIPMENT = ['landContractHolder'];
export const RELATIONSHIPS_WITH_DESCRIPTION_ONLY = ['billToOther'];
export const RELATIONSHIPS_WITH_ALL_FIELDS = ['partnerOrCoOwner', 'additionalInterest'];

export const ENTITY_TYPE_OPTIONS = [
  { id: 'partner', value: 'Partner' },
  { id: 'coOwner', value: 'Co-owner' }
];

export const getDescriptionLabel = (relationship) => {
  if (relationship === 'personOrOrganization') {
    return "Describe your interest in each location you've selected";
  }
  if (RELATIONSHIPS_WITH_ALL_FIELDS.includes(relationship)) {
    return 'Describe the interest in each item(s) or location(s) selected';
  }
  return 'Description';
};

export const getVisibilityFlags = (relationship) => ({
  showLocationFields: RELATIONSHIPS_WITH_LOCATION.includes(relationship),
  showEquipmentFields: RELATIONSHIPS_WITH_EQUIPMENT.includes(relationship),
  showLocationAndRiskFields: RELATIONSHIPS_WITH_LOCATION_AND_RISKS.includes(relationship),
  showLocationRiskAndEquipmentFields: RELATIONSHIPS_WITH_LOCATION_RISKS_AND_EQUIPMENT.includes(relationship),
  showDescriptionOnly: RELATIONSHIPS_WITH_DESCRIPTION_ONLY.includes(relationship),
  showAllFields: RELATIONSHIPS_WITH_ALL_FIELDS.includes(relationship)
});

// Maps each relationship type to the entity field arrays it supports.
// Fields not listed for a relationship are cleared before save to prevent stale data.
const RELATIONSHIP_ENTITY_FIELDS = {
  personOrOrganization: ['locations'],
  mortgageeCoOwner: ['locations'],
  managerOrLessorOfPremises: ['locations'],
  lossPayee: ['locations'],
  landlord: ['locations'],
  occupant: ['locations'],
  lessorOfLeasedEquipment: ['equipment'],
  trustCorpsEtc: ['locations', 'risks'],
  landContractHolder: ['locations', 'risks', 'equipment'],
  billToOther: [],
  partnerOrCoOwner: ['locations', 'risks', 'equipment', 'sppItems', 'rpsItems'],
  additionalInterest: ['locations', 'risks', 'equipment', 'sppItems', 'rpsItems']
};

export const filterFieldsByRelationship = (fields, relationship) => {
  const allowed = RELATIONSHIP_ENTITY_FIELDS[relationship];
  if (!allowed) {
    return fields;
  }
  return {
    locations: allowed.includes('locations') ? fields.locations : [],
    risks: allowed.includes('risks') ? fields.risks : [],
    equipment: allowed.includes('equipment') ? fields.equipment : [],
    sppItems: allowed.includes('sppItems') ? fields.sppItems : [],
    rpsItems: allowed.includes('rpsItems') ? fields.rpsItems : []
  };
};

/**
 * Converts backend AssociatedEntity[] → flat arrays for chipArray form fields.
 *
 * Backend stores structured references:
 *   [{ entityType: 'LOCATION', locationId: 'L1' },
 *    { entityType: 'RISK', locationId: 'L1', riskId: 'R1' },
 *    { entityType: 'EQUIPMENT', locationId: 'L1', itemId: 'E1' }]
 *
 * Form expects flat arrays with composite IDs matching option helpers:
 *   { locations: ['L1'], risks: ['L1::R1'], equipment: ['L1::R1::E1'], sppItems: ['L1::R1::S1'], rpsItems: [] }
 */
export const associatedEntitiesToFormFields = (associatedEntities) => {
  const locations = [];
  const risks = [];
  const equipment = [];
  const sppItems = [];
  const rpsItems = [];

  for (const entity of associatedEntities || []) {
    switch (entity.entityType) {
      case 'LOCATION':
        locations.push(entity.locationId);
        break;
      case 'RISK':
        risks.push(`${entity.locationId}::${entity.riskId}`);
        break;
      case 'EQUIPMENT':
        equipment.push(`${entity.locationId}::${entity.riskId}::${entity.itemId}`);
        break;
      case 'SPP':
        sppItems.push(`${entity.locationId}::${entity.riskId}::${entity.itemId}`);
        break;
      case 'RELATED_PRIVATE_STRUCTURE':
        rpsItems.push(`${entity.locationId}::${entity.riskId}::${entity.itemId}`);
        break;
      default:
        break;
    }
  }

  return { locations, risks, equipment, sppItems, rpsItems };
};

/**
 * Converts flat form arrays → backend AssociatedEntity[].
 *
 * Parses composite IDs (delimited by '::') to extract locationId, riskId, itemId.
 */
export const formFieldsToAssociatedEntities = ({ locations, risks, equipment, sppItems, rpsItems }) => {
  const entities = [];

  for (const locationId of locations || []) {
    entities.push({ entityType: 'LOCATION', locationId });
  }

  for (const compositeId of risks || []) {
    const [locationId, riskId] = compositeId.split('::');
    entities.push({ entityType: 'RISK', locationId, riskId });
  }

  for (const compositeId of equipment || []) {
    const [locationId, riskId, itemId] = compositeId.split('::');
    entities.push({ entityType: 'EQUIPMENT', locationId, riskId, itemId });
  }

  for (const compositeId of sppItems || []) {
    const [locationId, riskId, itemId] = compositeId.split('::');
    entities.push({ entityType: 'SPP', locationId, riskId, itemId });
  }

  for (const compositeId of rpsItems || []) {
    const [locationId, riskId, itemId] = compositeId.split('::');
    entities.push({ entityType: 'RELATED_PRIVATE_STRUCTURE', locationId, riskId, itemId });
  }

  return entities;
};

/**
 * Hydrates a farm interested party from backend format (associatedEntities)
 * into form format (flat arrays) for use with chipArray fields.
 */
export const hydrateFarmPartyForForm = (party) => {
  if (!party) {
    return party;
  }
  const formFields = associatedEntitiesToFormFields(party.associatedEntities);
  return { ...party, ...formFields };
};

/**
 * Prepares a farm interested party from form format (flat arrays)
 * into backend format (associatedEntities) for save/mutation.
 */
export const prepareFarmPartyForSave = (party) => {
  if (!party) {
    return party;
  }
  const { locations, risks, equipment, sppItems, rpsItems, ...rest } = party;
  const filtered = filterFieldsByRelationship({ locations, risks, equipment, sppItems, rpsItems }, party.relationship);
  const associatedEntities = formFieldsToAssociatedEntities(filtered);
  return { ...rest, associatedEntities };
};
